/**
 * RegisterPage.jsx
 * Il Diario di Bordo - SaaS
 * 
 * Pagina di registrazione per Organization + Responsabile
 * 
 * Flusso:
 * - Step 1: Email + Password (account Responsabile)
 * - Step 2: Nome Organization + Tipo Ente
 * - Step 3: Nome/Cognome Responsabile + Accettazione TOS/Privacy (scroll obbligatorio)
 * 
 * @version 2.1.0 - UI migliorata documenti legali (layout 2 colonne, ReactMarkdown)
 * @updated Gennaio 2025
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { useNotification } from '../../contexts/NotificationContext'
import api from '../../services/api'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Mail,
  Building2,
  User,
  Eye,
  EyeOff,
  FileText,
  AlertCircle,
  ExternalLink,
  ScrollText
} from 'lucide-react'

// Tipi di ente accettati
const ENTITY_TYPES = [
  { value: 'cooperativa', label: 'Cooperativa Sociale' },
  { value: 'onlus', label: 'ONLUS' },
  { value: 'associazione', label: 'Associazione' },
  { value: 'fondazione', label: 'Fondazione' },
  { value: 'ente_pubblico', label: 'Ente Pubblico' },
  { value: 'privato', label: 'Privato' },
  { value: 'altro', label: 'Altro' }
]

/**
 * Componente per visualizzare un documento legale con scroll obbligatorio
 * Design migliorato con card layout e ReactMarkdown
 */
function LegalDocumentViewer({ 
  document, 
  onScrollComplete, 
  isScrollComplete,
  isChecked,
  onCheckChange,
  error
}) {
  const containerRef = useRef(null)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)

  // Verifica se l'utente ha scrollato fino in fondo
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return
    
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    // Considera "in fondo" quando mancano meno di 50px
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50
    
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true)
      onScrollComplete()
    }
  }, [hasScrolledToBottom, onScrollComplete])

  // Se il documento è molto corto, considera già scrollato
  useEffect(() => {
    if (containerRef.current) {
      const { scrollHeight, clientHeight } = containerRef.current
      if (scrollHeight <= clientHeight) {
        setHasScrolledToBottom(true)
        onScrollComplete()
      }
    }
  }, [document, onScrollComplete])

  if (!document) {
    return (
      <div className="h-full flex flex-col border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="text-sm text-gray-500 mt-3">Caricamento documento...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
      {/* Header documento */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center min-w-0">
          <FileText className="h-5 w-5 mr-2 text-primary-600 flex-shrink-0" />
          <h4 className="font-semibold text-gray-900 dark:text-white truncate">
            {document.title || document.typeDisplayName}
          </h4>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
          <span className="text-xs text-gray-500 bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded">
            v{document.version}
          </span>
          {document.content?.externalUrl && (
            <a 
              href={document.content.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center"
            >
              Apri
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          )}
        </div>
      </div>

      {/* Container scrollabile */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 text-sm text-gray-700 dark:text-gray-300 legal-doc-scroll"
        style={{ maxHeight: '320px', minHeight: '280px' }}
      >
        {/* Contenuto del documento con ReactMarkdown */}
        {document.content?.body ? (
          <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-li:text-gray-600 dark:prose-li:text-gray-300 prose-strong:text-gray-800 dark:prose-strong:text-gray-200">
            <ReactMarkdown>{document.content.body}</ReactMarkdown>
          </div>
        ) : document.content?.summary ? (
          <div className="space-y-4">
            <p>{document.content.summary}</p>
            {document.content?.externalUrl && (
              <a 
                href={document.content.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-primary-600 hover:text-primary-700"
              >
                Leggi documento completo
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            )}
          </div>
        ) : document.pdf?.url ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-500 mb-3">Documento PDF disponibile</p>
            <a 
              href={document.pdf.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
            >
              Apri PDF
              <ExternalLink className="h-4 w-4 ml-1" />
            </a>
          </div>
        ) : (
          <p className="text-gray-500 italic">Contenuto del documento non disponibile.</p>
        )}
      </div>

      {/* Footer con checkbox */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
        {/* Indicatore scroll */}
        {!hasScrolledToBottom && (
          <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center mb-2">
            <ScrollText className="h-3 w-3 mr-1.5 flex-shrink-0" />
            Scorri fino in fondo per poter accettare
          </p>
        )}

        {/* Checkbox accettazione */}
        <label className={`flex items-center space-x-3 p-2.5 rounded-lg border transition-all cursor-pointer
          ${error ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20' : 
            isChecked ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20' :
            'border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700/50'}
          ${!isScrollComplete ? 'opacity-50 cursor-not-allowed' : ''}
        `}>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => onCheckChange(e.target.checked)}
            disabled={!isScrollComplete}
            className="h-4 w-4 text-primary-600 rounded border-gray-300 
                       focus:ring-primary-500 focus:ring-offset-0
                       disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300 select-none">
            Ho letto e accetto
          </span>
          {isChecked && <Check className="h-4 w-4 text-green-500 ml-auto" />}
        </label>
        {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
      </div>
    </div>
  )
}

export default function RegisterPage() {
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  // Documenti legali
  const [legalDocs, setLegalDocs] = useState({ terms: null, privacy: null })
  const [loadingDocs, setLoadingDocs] = useState(false)
  const [docsError, setDocsError] = useState(null)

  // Stato scroll documenti
  const [termsScrolled, setTermsScrolled] = useState(false)
  const [privacyScrolled, setPrivacyScrolled] = useState(false)

  // Form data
  const [formData, setFormData] = useState({
    // Step 1 - Account
    email: '',
    password: '',
    confirmPassword: '',
    // Step 2 - Organization
    organizationName: '',
    entityType: 'cooperativa',
    // Step 3 - Responsabile + Consensi
    nome: '',
    cognome: '',
    acceptTerms: false,
    acceptPrivacy: false
  })

  const [errors, setErrors] = useState({})
  const { showError, showSuccess } = useNotification()
  const navigate = useNavigate()

  // Carica documenti legali quando si arriva allo step 3
  useEffect(() => {
    if (step === 3 && !legalDocs.terms && !legalDocs.privacy && !loadingDocs) {
      loadLegalDocuments()
    }
  }, [step])

  const loadLegalDocuments = async () => {
    setLoadingDocs(true)
    setDocsError(null)
    
    try {
      // Carica TOS e Privacy in parallelo
      const [termsRes, privacyRes] = await Promise.all([
        api.get('/legal/documents/type/tos').catch(() => ({ data: { data: { document: null } } })),
        api.get('/legal/documents/type/privacy').catch(() => ({ data: { data: { document: null } } }))
      ])

      setLegalDocs({
        terms: termsRes.data?.data?.document || null,
        privacy: privacyRes.data?.data?.document || null
      })

      // Se non troviamo i documenti, mostra errore
      if (!termsRes.data?.data?.document || !privacyRes.data?.data?.document) {
        setDocsError('Alcuni documenti legali non sono disponibili. Contatta il supporto.')
      }
    } catch (error) {
      console.error('Errore caricamento documenti legali:', error)
      setDocsError('Impossibile caricare i documenti legali. Riprova più tardi.')
    } finally {
      setLoadingDocs(false)
    }
  }

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  // === VALIDAZIONI ===

  const validateStep1 = () => {
    const newErrors = {}
    
    if (!formData.email) {
      newErrors.email = 'Email obbligatoria'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email non valida'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password obbligatoria'
    } else {
      if (formData.password.length < 8) {
        newErrors.password = 'Minimo 8 caratteri'
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Deve contenere almeno una maiuscola'
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Deve contenere almeno un numero'
      } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
        newErrors.password = 'Deve contenere almeno un carattere speciale (!@#$%...)'
      }
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Le password non corrispondono'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = () => {
    const newErrors = {}
    
    if (!formData.organizationName) {
      newErrors.organizationName = 'Nome organizzazione obbligatorio'
    } else if (formData.organizationName.length < 3) {
      newErrors.organizationName = 'Minimo 3 caratteri'
    }
    
    if (!formData.entityType) {
      newErrors.entityType = 'Seleziona il tipo di ente'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep3 = () => {
    const newErrors = {}
    
    if (!formData.nome) newErrors.nome = 'Nome obbligatorio'
    if (!formData.cognome) newErrors.cognome = 'Cognome obbligatorio'
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Devi accettare i Termini di Servizio'
    }
    if (!formData.acceptPrivacy) {
      newErrors.acceptPrivacy = 'Devi accettare l\'Informativa Privacy'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // === NAVIGAZIONE ===

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2)
    else if (step === 2 && validateStep2()) setStep(3)
  }

  const handleSubmit = async () => {
    if (!validateStep3()) return
    
    setLoading(true)
    try {
      // Nuovo formato per il backend organizationOnboarding
      await api.post('/onboarding/organization/register', {
        organization: {
          name: formData.organizationName,
          entityType: formData.entityType
        },
        responsabile: {
          email: formData.email,
          password: formData.password,
          nome: formData.nome,
          cognome: formData.cognome
        },
        acceptTerms: true,
        acceptPrivacy: true
      })
      
      setEmailSent(true)
      showSuccess('Registrazione completata! Controlla la tua email.')
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante la registrazione'
      showError(message)
      
      // Se l'errore è relativo a un campo specifico, evidenzialo
      if (error.response?.data?.field) {
        const field = error.response.data.field
        if (field.includes('email')) {
          setErrors(prev => ({ ...prev, email: message }))
          setStep(1) // Torna allo step dell'email
        }
      }
    } finally {
      setLoading(false)
    }
  }

  // === RENDER: Email inviata ===

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-primary py-12 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Controlla la tua email!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Abbiamo inviato un link di verifica a<br />
            <strong>{formData.email}</strong>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Dopo aver verificato l'email, dovrai configurare l'autenticazione a due fattori (2FA) 
            per proteggere i dati sensibili.
          </p>
          <Link 
            to="/login" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna al login
          </Link>
        </div>
      </div>
    )
  }

  // === RENDER: Form registrazione ===
  // Container più largo per step 3 (documenti legali side by side)
  const containerClass = step === 3 
    ? 'max-w-4xl' 
    : 'max-w-md'

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-primary py-12 px-4">
      <div className={`${containerClass} w-full transition-all duration-300`}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <span className="text-4xl">📘</span>
            <h1 className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
              Registra la tua Organizzazione
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Crea un account come Responsabile
            </p>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                  ${step > s ? 'bg-green-500 text-white' : 
                    step === s ? 'bg-primary-600 text-white' : 
                    'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'}`}
                >
                  {step > s ? <Check className="h-4 w-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-1 mx-1 rounded transition-colors
                    ${step > s ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-600'}`} 
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1 - Account */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <Mail className="h-8 w-8 mx-auto text-primary-600 mb-2" />
                <h2 className="font-semibold text-gray-900 dark:text-white">Crea il tuo account</h2>
                <p className="text-sm text-gray-500">Questa sarà la tua email di accesso</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email *
                </label>
                <input 
                  type="email" 
                  value={formData.email} 
                  onChange={(e) => updateField('email', e.target.value)}
                  className={`input ${errors.email ? 'border-red-500' : ''}`} 
                  placeholder="responsabile@organizzazione.it" 
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    className={`input pr-10 ${errors.password ? 'border-red-500' : ''}`} 
                    placeholder="Minimo 8 caratteri" 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)} 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                <p className="mt-1 text-xs text-gray-500">Maiuscola, numero e carattere speciale richiesti</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Conferma Password *
                </label>
                <input 
                  type="password" 
                  value={formData.confirmPassword} 
                  onChange={(e) => updateField('confirmPassword', e.target.value)}
                  className={`input ${errors.confirmPassword ? 'border-red-500' : ''}`} 
                  placeholder="Ripeti la password" 
                />
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
              </div>
            </div>
          )}

          {/* Step 2 - Organization */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <Building2 className="h-8 w-8 mx-auto text-primary-600 mb-2" />
                <h2 className="font-semibold text-gray-900 dark:text-white">La tua Organizzazione</h2>
                <p className="text-sm text-gray-500">Potrai gestire uno o più centri</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome Organizzazione *
                </label>
                <input 
                  type="text" 
                  value={formData.organizationName} 
                  onChange={(e) => updateField('organizationName', e.target.value)}
                  className={`input ${errors.organizationName ? 'border-red-500' : ''}`} 
                  placeholder="Es: Cooperativa La Stella" 
                />
                {errors.organizationName && <p className="mt-1 text-sm text-red-500">{errors.organizationName}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo di Ente *
                </label>
                <select 
                  value={formData.entityType} 
                  onChange={(e) => updateField('entityType', e.target.value)}
                  className={`input ${errors.entityType ? 'border-red-500' : ''}`}
                >
                  {ENTITY_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                {errors.entityType && <p className="mt-1 text-sm text-red-500">{errors.entityType}</p>}
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Nota:</strong> I dati fiscali (P.IVA, PEC, ecc.) e l'indirizzo legale 
                  saranno richiesti dopo la registrazione, prima dell'attivazione dell'abbonamento.
                </p>
              </div>
            </div>
          )}

          {/* Step 3 - Responsabile + Consensi */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <User className="h-8 w-8 mx-auto text-primary-600 mb-2" />
                <h2 className="font-semibold text-gray-900 dark:text-white">I tuoi dati</h2>
                <p className="text-sm text-gray-500">Sarai il Responsabile dell'organizzazione</p>
              </div>
              
              {/* Dati personali - centrati */}
              <div className="max-w-md mx-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nome *
                    </label>
                    <input 
                      type="text" 
                      value={formData.nome} 
                      onChange={(e) => updateField('nome', e.target.value)}
                      className={`input ${errors.nome ? 'border-red-500' : ''}`} 
                      placeholder="Nome" 
                    />
                    {errors.nome && <p className="mt-1 text-sm text-red-500">{errors.nome}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Cognome *
                    </label>
                    <input 
                      type="text" 
                      value={formData.cognome} 
                      onChange={(e) => updateField('cognome', e.target.value)}
                      className={`input ${errors.cognome ? 'border-red-500' : ''}`} 
                      placeholder="Cognome" 
                    />
                    {errors.cognome && <p className="mt-1 text-sm text-red-500">{errors.cognome}</p>}
                  </div>
                </div>
              </div>

              {/* Documenti legali - LAYOUT 2 COLONNE */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center justify-center">
                  <FileText className="h-5 w-5 mr-2 text-primary-600" />
                  Documenti legali
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                  Leggi attentamente e scorri fino in fondo per poter accettare
                </p>

                {loadingDocs ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary-600" />
                    <p className="text-sm text-gray-500 mt-3">Caricamento documenti...</p>
                  </div>
                ) : docsError ? (
                  <div className="max-w-md mx-auto p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">{docsError}</p>
                    <button 
                      onClick={loadLegalDocuments}
                      className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Riprova
                    </button>
                  </div>
                ) : (
                  /* Grid 2 colonne per documenti legali */
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Termini di Servizio */}
                    <LegalDocumentViewer
                      document={legalDocs.terms}
                      onScrollComplete={() => setTermsScrolled(true)}
                      isScrollComplete={termsScrolled}
                      isChecked={formData.acceptTerms}
                      onCheckChange={(checked) => updateField('acceptTerms', checked)}
                      error={errors.acceptTerms}
                    />

                    {/* Privacy Policy */}
                    <LegalDocumentViewer
                      document={legalDocs.privacy}
                      onScrollComplete={() => setPrivacyScrolled(true)}
                      isScrollComplete={privacyScrolled}
                      isChecked={formData.acceptPrivacy}
                      onCheckChange={(checked) => updateField('acceptPrivacy', checked)}
                      error={errors.acceptPrivacy}
                    />
                  </div>
                )}
              </div>

              {/* Riepilogo */}
              <div className="max-w-md mx-auto mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Riepilogo</h3>
                <div className="text-sm space-y-1">
                  <p><span className="text-gray-500">Email:</span> {formData.email}</p>
                  <p><span className="text-gray-500">Organizzazione:</span> {formData.organizationName}</p>
                  <p>
                    <span className="text-gray-500">Tipo:</span>{' '}
                    {ENTITY_TYPES.find(t => t.value === formData.entityType)?.label}
                  </p>
                  <p><span className="text-gray-500">Responsabile:</span> {formData.nome} {formData.cognome}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="mt-8 flex justify-between">
            {step > 1 ? (
              <button 
                onClick={() => setStep(step - 1)} 
                className="btn-secondary flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Indietro
              </button>
            ) : (
              <Link to="/login" className="btn-secondary flex items-center">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Login
              </Link>
            )}
            
            {step < 3 ? (
              <button 
                onClick={handleNext} 
                className="btn-primary flex items-center"
              >
                Avanti
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            ) : (
              <button 
                onClick={handleSubmit} 
                disabled={loading || loadingDocs || (!formData.acceptTerms || !formData.acceptPrivacy)}
                className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Registrazione...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Completa
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-white/70">
          © {new Date().getFullYear()} Il Diario di Bordo
        </p>
      </div>
    </div>
  )
}
